import React, { useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import Home from "./pages/Home/Home.js";
import SingModif from "./pages/SignModif/SignModif.js";
import Suivi from "./pages/Suivis/Suivi.js";
import Visuel from "./pages/SignModif/components/DocSing.js";
import Admin from "./pages/Admin/Admin.js";
import DetailsModal from "./pages/SignModif/components/DetailsSign.js";
import Mandat from "./pages/Portefeuille/Mandat.js";
import ReservationPublique from "./pages/ReservationPublique/ReservationPublique.js";
import Login from "./pages/Login/Login.js";
import { EtatGlobalProvider, useEtatGlobal } from "./pages/EtatGlobal.js";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { GoogleOAuthProvider, googleLogout } from "@react-oauth/google";
import { Loader2, ShieldCheck } from "lucide-react";
import { DialogProvider } from "./components/ui/Dialog";
import SessionExpiredModal from "./components/SessionExpiredModal";
import ConnectGoogleServicesModal from "./components/ConnectGoogleServicesModal";

import { Analytics } from "@vercel/analytics/react";
import { authFetch } from "./HttpClient.js";

// Ne pas re-ping à chaque F5/remontage de l'app dans la même session de
// travail — seulement si la dernière "venue" enregistrée date de plus de
// 30 min, sinon le compteur de connexions du Super Admin exploserait pour
// rien à chaque rechargement de page.
const SESSION_PING_THROTTLE_MS = 30 * 60 * 1000;

function pingSessionIfDue() {
  const lastPing = Number(localStorage.getItem("lastSessionPingAt") || 0);
  if (Date.now() - lastPing < SESSION_PING_THROTTLE_MS) return;

  localStorage.setItem("lastSessionPingAt", String(Date.now()));
  authFetch("https://backend-myalfa.vercel.app/api/users/session-ping", {
    method: "POST",
  }).catch((err) => console.error("Erreur lors du ping de session :", err));
}

function AppContent() {
  const { utilisateur, setUtilisateur, impersonatorSession, stopImpersonation } =
    useEtatGlobal();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    // Une session MyAlfa déjà valide (JWT stocké lors d'une précédente
    // connexion Google) est réutilisée silencieusement — pas de nouveau
    // prompt Google à chaque chargement. C'est HttpClient.js (déjà
    // indépendant de ce flow) qui gère l'expiration réelle via son
    // interception 401 `expired: true` → événement "session-expired".
    const token = localStorage.getItem("token");
    const storedUser = localStorage.getItem("user");
    if (token && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        setUtilisateur(parsedUser);
        pingSessionIfDue();
      } catch (err) {
        console.error("Session locale corrompue:", err);
      }
    }
    setLoading(false);
  }, [setUtilisateur]);

  const handleAuthenticated = (authenticatedUser) => {
    setUser(authenticatedUser);
    setUtilisateur(authenticatedUser);
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("current_view");
    localStorage.removeItem("myalfa-bureau-profile");
    setUser(null);
    try {
      googleLogout();
    } catch (_) {}
  };

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-white shadow-2xl rounded-2xl p-10 w-[400px] text-center space-y-6">
          <div className="flex justify-center">
            <div className="bg-[#840040]/10 p-4 rounded-full">
              <ShieldCheck className="w-10 h-10 text-[#840040]" />
            </div>
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-800">
              Vérification en cours
            </h2>
            <p className="text-sm text-gray-500 mt-2">
              Nous vérifions vos informations utilisateur...
            </p>
          </div>
          <div className="flex justify-center">
            <Loader2 className="w-8 h-8 text-[#840040] animate-spin" />
          </div>
        </div>
      </div>
    );

  // Page publique de réservation (Planning) — jamais derrière la connexion
  // staff, un client externe n'a pas de compte MyALFA (voir server.js
  // allowlist /api/reservation/public/:token, la même logique côté API).
  if (window.location.pathname.startsWith("/rdv/")) {
    return (
      <Routes>
        <Route path="/rdv/:token" element={<ReservationPublique />} />
      </Routes>
    );
  }

  if (!user) return <Login onLogin={handleAuthenticated} />;

  return (
    <>
      {impersonatorSession && (
        <div className="bg-amber-500 text-white text-sm px-4 py-2 flex items-center justify-center gap-3 sticky top-0 z-[100]">
          Connecté en tant que {utilisateur?.name} {utilisateur?.lastName}
          <button onClick={stopImpersonation} className="underline font-semibold">
            Revenir à mon compte
          </button>
        </div>
      )}
      {/* Pas pendant une usurpation de compte : connecterait par erreur le
          Gmail/Agenda personnel de l'administrateur au compte usurpé. */}
      {!impersonatorSession && <ConnectGoogleServicesModal />}
      <Routes>
        <Route path="/home/*" element={<Home onLogout={handleLogout} />} />
        <Route path="/SingModif" element={<SingModif />} />
        <Route path="/Suivi" element={<Suivi />} />
        <Route path="/Admin" element={<Admin />} />
        <Route path="/Visuel" element={<Visuel />} />
        <Route path="/Details" element={<DetailsModal />} />
        <Route path="/Mandat" element={<Mandat />} />
        <Route path="*" element={<Navigate to="/home" />} />
      </Routes>
    </>
  );
}

const queryClient = new QueryClient();

function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <QueryClientProvider client={queryClient}>
        <EtatGlobalProvider>
          <Router>
            <AppContent />
            <Analytics />
          </Router>
          <DialogProvider />
          <SessionExpiredModal />
        </EtatGlobalProvider>
      </QueryClientProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
